/**
 * First-party frontend activity tracking.
 *
 * Posts page views and tracked actions to the backend (POST /api/frontend-activity),
 * which records them in frontend_activity_log for the admin Access & Activity
 * report (Sandbox Management page). Fully fire-and-forget: failures are swallowed
 * so tracking can never affect the UX. Only fires when a user is logged in.
 *
 * This is the first-party alternative to Datadog RUM for per-user page/function
 * access reporting (RUM has no user attribution wired). Page "navigation" in this
 * app is a tab switch (app.tsx activeTab), so trackPageView is called on tab change.
 */
import { authFetch } from '@/lib/authFetch'
import { trackAction as ddTrackAction } from '@/src/providers/DatadogProvider'

// Stable per-tab session id so a report can group a user's events into a visit.
function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem('fe_activity_session')
    if (!sid) {
      sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
      sessionStorage.setItem('fe_activity_session', sid)
    }
    return sid
  } catch {
    return 'nosession'
  }
}

function isLoggedIn(): boolean {
  try {
    return !!localStorage.getItem('auth_token')
  } catch {
    return false
  }
}

let lastPageViewKey = ''

// What the user is looking at, so section and field events can say where they
// happened. Reset on every page view (TRUS-664).
let currentPage: { path: string; title?: string } | null = null
let currentSection = ''
let fieldsSeen = new Set<string>()

/** The sidebar's own label for a page (V2Sidebar sets data-nav-label). The
 *  browser title is always "WeaveIn", so it says nothing about the page. */
function navLabelFor(path: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  try {
    const el = document.querySelector<HTMLElement>(`[data-nav-tab="${CSS.escape(path)}"]`)
    return el?.dataset.navLabel || undefined
  } catch {
    return undefined
  }
}

function post(payload: Record<string, unknown>): void {
  if (!isLoggedIn()) return
  // Fire-and-forget — never await, never surface errors.
  try {
    void authFetch('/api/frontend-activity', {
      method: 'POST',
      jsonBody: { ...payload, session_id: getSessionId() },
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* ignore */
  }
}

/** Record a page/tab view. De-dupes immediate repeats of the same path. */
export function trackPageView(path: string, pageTitle?: string): void {
  if (!path) return
  const key = `${path}|${pageTitle || ''}`
  if (key === lastPageViewKey) return
  lastPageViewKey = key
  const title = pageTitle ?? navLabelFor(path)
  currentPage = { path, title }
  currentSection = ''
  fieldsSeen = new Set()
  post({
    event_type: 'page_view',
    path,
    // Never document.title: it is "WeaveIn" on every page. Without a sidebar
    // label the backend names the page from the nav registry.
    page_title: title,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
  })
}

/**
 * Record a discrete user action. Fires BOTH:
 *   1) Datadog RUM (addAction) — granular, queryable in RUM Explorer / session replay
 *   2) first-party /api/frontend-activity — surfaces in the in-app Access & Activity report
 *
 * Prefer one of the registered keys in src/lib/trackedActions.ts (TRACKED_ACTIONS)
 * so the action appears in the Tracked Actions catalog with a label/description.
 */
export function trackAction(actionName: string, context?: Record<string, unknown>): void {
  if (!actionName) return
  try {
    ddTrackAction(actionName, context)
  } catch {
    /* ignore — Datadog optional */
  }
  post({
    event_type: 'action',
    action_name: actionName,
    path: typeof context?.path === 'string' ? context.path : undefined,
  })
}

// ---- Section and field tracking (TRUS-664) ----------------------------------
// Two capturing listeners cover the whole app, so no page has to opt in:
//   * a click on any role="tab" element  -> section_view (the tab's label)
//   * a change on any input/select/textarea -> field_edit (the field's LABEL)
// A field's VALUE is never read or sent. Password and hidden inputs are skipped,
// and each field is recorded at most once per page visit. Mark a region
// data-no-track to exclude it.

const clean = (s: string | null | undefined, max = 120): string =>
  (s || '').replace(/\s+/g, ' ').replace(/\s*\*\s*$/, '').trim().slice(0, max)

/** Text of a label's own text nodes — NOT textContent, which would include the
 *  text of a nested textarea or select (i.e. what the user typed or chose). */
function ownText(el: Element): string {
  let out = ''
  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) out += n.textContent || ''
    else if (n instanceof HTMLElement && !n.matches('input, select, textarea, option')) out += ownText(n)
  })
  return out
}

function fieldLabel(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
  const aria = el.getAttribute('aria-label')
  if (aria) return clean(aria)
  if (el.id) {
    try {
      const forLabel = document.querySelector(`label[for="${CSS.escape(el.id)}"]`)
      if (forLabel) return clean(ownText(forLabel))
    } catch {
      /* ignore */
    }
  }
  const labelledBy = el.getAttribute('aria-labelledby')
  if (labelledBy) {
    const ref = document.getElementById(labelledBy.split(' ')[0])
    if (ref) return clean(ownText(ref))
  }
  const wrapping = el.closest('label')
  if (wrapping) return clean(ownText(wrapping))
  return clean(el.getAttribute('name') || el.getAttribute('placeholder'))
}

function pageContext(): { path?: string; page_title?: string } {
  return { path: currentPage?.path, page_title: currentPage?.title }
}

let interactionTrackingInstalled = false

export function installInteractionTracking(): void {
  if (interactionTrackingInstalled || typeof document === 'undefined') return
  interactionTrackingInstalled = true

  document.addEventListener(
    'click',
    (e) => {
      try {
        const tab = (e.target as Element | null)?.closest?.('[role="tab"]') as HTMLElement | null
        if (!tab || tab.closest('[data-no-track]')) return
        const label = clean(tab.dataset.trackLabel || tab.getAttribute('aria-label') || ownText(tab))
        if (!label || label === currentSection) return
        currentSection = label
        post({ event_type: 'section_view', ...pageContext(), action_name: label })
      } catch {
        /* tracking must never affect the UI */
      }
    },
    true,
  )

  document.addEventListener(
    'change',
    (e) => {
      try {
        const el = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null
        if (!el || !el.matches?.('input, select, textarea')) return
        const type = (el.getAttribute('type') || '').toLowerCase()
        if (type === 'password' || type === 'hidden') return
        if (el.closest('[data-no-track]')) return
        const label = fieldLabel(el)
        if (!label) return
        const seenKey = `${currentSection}|${label}`
        if (fieldsSeen.has(seenKey)) return
        fieldsSeen.add(seenKey)
        post({
          event_type: 'field_edit',
          ...pageContext(),
          action_name: currentSection ? `${currentSection} › ${label}`.slice(0, 300) : label,
        })
      } catch {
        /* tracking must never affect the UI */
      }
    },
    true,
  )
}

// ---- Error tracking --------------------------------------------------------
// Feeds the observability agent: the errors a user ACTUALLY hit (failed API
// calls + JS crashes). Posts to /api/observability/frontend-error, which the
// backend dedupes by fingerprint and hands to the RCA pipeline. Same fire-and-
// forget discipline as trackAction — reporting an error must never throw.

export interface TrackedError {
  error_type?: string
  error_message?: string
  path?: string
  status_code?: number
  method?: string
  stack?: string
}

let lastErrorKey = ''
let lastErrorAt = 0

function postError(payload: TrackedError): void {
  if (!isLoggedIn()) return
  // Collapse a burst of the identical error (e.g. a render loop) within 5s.
  const key = `${payload.error_type || ''}|${payload.status_code || ''}|${payload.path || ''}|${payload.error_message || ''}`
  const now = Date.now()
  if (key === lastErrorKey && now - lastErrorAt < 5000) return
  lastErrorKey = key
  lastErrorAt = now
  try {
    void authFetch('/api/observability/frontend-error', {
      method: 'POST',
      jsonBody: { ...payload, session_id: getSessionId() },
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* ignore */
  }
}

/** Report a client-side error the user hit (failed request or caught exception). */
export function trackError(err: TrackedError): void {
  postError(err)
}

/** Install global handlers for uncaught errors + unhandled promise rejections.
 *  Call once at app boot (idempotent). */
let errorHandlersInstalled = false
export function installErrorTracking(): void {
  if (errorHandlersInstalled || typeof window === 'undefined') return
  errorHandlersInstalled = true
  window.addEventListener('error', (e: ErrorEvent) => {
    postError({
      error_type: e.error?.name || 'Error',
      error_message: e.message,
      path: typeof window !== 'undefined' ? window.location?.pathname : undefined,
      stack: e.error?.stack,
    })
  })
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason
    postError({
      error_type: reason?.name || 'UnhandledRejection',
      error_message: typeof reason === 'string' ? reason : reason?.message || String(reason),
      path: typeof window !== 'undefined' ? window.location?.pathname : undefined,
      stack: reason?.stack,
    })
  })
}
